import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, interval, Subject, map, take, tap, startWith, takeUntil, distinctUntilChanged } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import {
  ButtonTheme,
  ButtonType,
  NotificationTheme,
  NotificationLayout,
  LoaderTheme,
  IconPosition,
} from '@fcom/ui-components';
import { RootPaths } from '@fcom/core';
import { sortBy, compareAsJson, serializeParams } from '@fcom/core/utils';

import { TripsActions } from '../../store/actions';
import { State, TripsState, FetchTripsStatus } from '../../interfaces/store.interface';
import { selectTripsState } from '../../store/selectors';
import { Trip, OutboundJourney } from '../../interfaces/pax-trip.interface';

// The source journey data may omit passengerAmount, so the view model keeps it optional.
type UpcomingTripRow = Omit<OutboundJourney, 'passengerAmount'> & {
  passengerAmount?: number;
};

enum Status {
  INITIAL,
  LOADING,
  COMPLETE,
  EMPTY,
  ERROR,
}

@Component({
  selector: 'fin-loyalty-upcoming-trips',
  templateUrl: './loyalty-upcoming-trips.component.html',
  styleUrls: ['./loyalty-upcoming-trips.component.scss'],
  standalone: false,
})
export class LoyaltyUpcomingTripsComponent implements OnInit, AfterViewInit, OnDestroy {
  private store$ = inject<Store<State>>(Store);
  private languageService = inject(LanguageService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly IconPosition = IconPosition;
  readonly LoaderTheme = LoaderTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly Status = Status;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  trips$!: Observable<UpcomingTripRow[]>;
  visibleRows = 15;
  lastVisibleIndex!: number;
  status!: Status;
  tripState$: Observable<TripsState>;

  @ViewChildren('tripRow', { read: ElementRef }) tripRows!: QueryList<ElementRef<HTMLTableRowElement>>;

  private ngDestroyed$ = new Subject<void>();

  constructor() {
    this.tripState$ = this.store$.pipe(select(selectTripsState));
  }

  ngOnInit(): void {
    this.lastVisibleIndex = this.visibleRows - 1;
    this.store$.dispatch(TripsActions.fetchTrips());
    this.trips$ = this.getTrips();
    this.status = Status.INITIAL;
  }

  ngAfterViewInit(): void {
    this.tripRows.changes
      .pipe(startWith(this.tripRows), takeUntil(this.ngDestroyed$))
      .subscribe(() => this.revealRows());
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }

  onRevealRows(): void {
    this.lastVisibleIndex = this.lastVisibleIndex + this.visibleRows;
  }

  private getTrips(): Observable<UpcomingTripRow[]> {
    this.status = Status.LOADING;
    return this.tripState$.pipe(
      map((tripState) => {
        if (tripState.status === FetchTripsStatus.ERROR) {
          this.status = Status.ERROR;
        } else if (tripState.status === FetchTripsStatus.OK) {
          this.status = Status.COMPLETE;
          return sortBy(this.parseOutboundJourneys(tripState.trips), (trip) => trip.scheduledDateTimeUnix) || [];
        }
        return [];
      }),
      tap((trips: UpcomingTripRow[]) => {
        if (this.status !== Status.ERROR && this.status !== Status.LOADING && trips.length === 0) {
          this.status = Status.EMPTY;
        }
      }),
      distinctUntilChanged((a: UpcomingTripRow[], b: UpcomingTripRow[]): boolean => this.checkTripDistinction(a, b))
    );
  }

  private parseOutboundJourneys(trips: Trip[]): UpcomingTripRow[] {
    return trips.map((trip: Trip): UpcomingTripRow => {
      const outboundJourney = trip.journeys[0];
      return {
        departure: outboundJourney.departure.municipality,
        arrival: outboundJourney.arrival.municipality,
        scheduledDateTime: outboundJourney.departure.scheduledDateTime,
        scheduledDateTimeUnix: new Date(outboundJourney.departure.scheduledDateTime).getTime(),
        passengerAmount: outboundJourney.passengerAmount,
        bookingRef: trip.recLoc,
        mmbLink: this.generateMmbLink(trip.lastName, trip.recLoc),
      };
    });
  }

  private generateMmbLink(lastName: string, bookingRef: string): string {
    const bookingData = {
      lastName: lastName,
      recLoc: bookingRef,
    };

    return `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}?${serializeParams(bookingData)}`;
  }

  private revealRows(): void {
    const rowElements = this.tripRows
      ?.toArray()
      .map((row) => row.nativeElement)
      .filter((row) => !row.classList.contains('fade-up--animate'));

    if (!rowElements?.length) {
      return;
    }

    interval(150)
      .pipe(
        take(rowElements.length),
        map((index) => rowElements[index]),
        takeUntil(this.ngDestroyed$)
      )
      .subscribe((value) => {
        value.classList.add('fade-up--animate');
      });
  }

  private checkTripDistinction(a: UpcomingTripRow[], b: UpcomingTripRow[]): boolean {
    return compareAsJson(
      a.map((trip: UpcomingTripRow) => trip.bookingRef).sort((left, right) => left.localeCompare(right)),
      b.map((trip: UpcomingTripRow) => trip.bookingRef).sort((left, right) => left.localeCompare(right))
    );
  }
}
